/**
 * F-002 — Scene lighting.
 *
 * Two light sources balance "bright planet faces visible" with "deep space
 * mood": a dim ambient term so nothing reads as pure black, plus a single
 * directional sun whose offset-from-camera angle gives planets a sculpted
 * crescent (familiar visual language for celestial bodies).
 */

import { AmbientLight, DirectionalLight, type Light } from "three";

const AMBIENT_INTENSITY = 0.18;
const SUN_INTENSITY = 1.05;
const AMBIENT_COLOR = 0xb4b8ff;
const SUN_COLOR = 0xfff4c2;

export interface SceneLights {
  ambient: AmbientLight;
  sun: DirectionalLight;
  all: ReadonlyArray<Light>;
}

export function createSceneLights(): SceneLights {
  const ambient = new AmbientLight(AMBIENT_COLOR, AMBIENT_INTENSITY);
  ambient.name = "scene-ambient";

  const sun = new DirectionalLight(SUN_COLOR, SUN_INTENSITY);
  sun.name = "scene-sun";
  // Offset the sun so spherical bodies show terminator shading rather than
  // flat front-lighting. Coordinates chosen by eye to land between the
  // camera (+Z) and screen-right.
  sun.position.set(8, 6, 12);
  sun.target.position.set(0, 0, 0);

  return { ambient, sun, all: [ambient, sun] };
}

export const __testing = {
  AMBIENT_INTENSITY,
  SUN_INTENSITY,
  AMBIENT_COLOR,
  SUN_COLOR,
};
